import React, { useState, useEffect } from 'react';
import { getAvailabilityBlocks, createAvailabilityBlock, deleteAvailabilityBlock } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../utils/apiError';
import { Plus, Clock, Calendar, Trash2, Ban } from 'lucide-react';

export default function AvailabilityBlocks() {
  const [blocks, setBlocks] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    is_full_day: true,
    reason: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const fetchBlocks = async () => {
    try {
      const response = await getAvailabilityBlocks();
      // Laravel devuelve {data: [...]}, Axios extrae el data, entonces accedemos a .data.data
      setBlocks(Array.isArray(response.data?.data) ? response.data.data : (Array.isArray(response.data) ? response.data : []));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al cargar bloqueos'));
    }
  };

  useEffect(() => {
    fetchBlocks();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const payload = {
        start_date: formData.start_date,
        end_date: formData.end_date || formData.start_date,
        is_full_day: formData.is_full_day,
        reason: formData.reason,
      };
      
      if (!formData.is_full_day) {
        payload.start_time = formData.start_time;
        payload.end_time = formData.end_time;
      }
      
      await createAvailabilityBlock(payload);
      toast.success('Bloqueo creado. Las citas afectadas fueron marcadas para reagendar.');
      setDrawerOpen(false);
      setFormData({
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: '',
        is_full_day: true,
        reason: '',
      });
      fetchBlocks();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al crear bloqueo'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este bloqueo?')) return;
    try {
      await deleteAvailabilityBlock(id);
      toast.success('Bloqueo eliminado');
      fetchBlocks();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#2B2D42] font-['Manrope']">
            Bloqueos de Disponibilidad
          </h1>
          <p className="text-[#5D5C61] mt-1">
            Gestiona los períodos donde no hay atención
          </p>
        </div>
        <Button
          className="bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
          onClick={() => setDrawerOpen(true)}
          data-testid="blocks-new-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Bloqueo
        </Button>
      </div>

      {blocks.length > 0 ? (
        <div className="grid gap-4">
          {blocks.map((block) => (
            <Card
              key={block.id}
              className="bg-white border-slate-100"
              data-testid={`block-card-${block.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#8D99AE]/10 flex items-center justify-center">
                      <Ban className="w-5 h-5 text-[#8D99AE]" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">
                        {block.is_full_day ? 'Día completo bloqueado' : 'Horario bloqueado'}
                      </CardTitle>
                      <CardDescription>
                        {block.reason || 'Sin motivo especificado'}
                      </CardDescription>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-[#9E2A2B] hover:bg-[#9E2A2B]/10"
                    onClick={() => handleDelete(block.id)}
                    data-testid={`block-delete-${block.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-6 text-sm text-[#5D5C61]">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {block.date_label}
                    </span>
                  </div>
                  {!block.is_full_day && block.start_time && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{block.start_time} - {block.end_time}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Clock className="w-12 h-12 text-[#8D99AE] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[#2B2D42]">No hay bloqueos</h3>
          <p className="text-[#5D5C61] mt-1">
            Crea un bloqueo cuando no puedas atender
          </p>
        </div>
      )}

      {/* Block Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-full sm:max-w-md" data-testid="block-drawer">
          <SheetHeader>
            <SheetTitle className="text-2xl font-['Manrope']">Nuevo Bloqueo</SheetTitle>
            <SheetDescription>
              Las citas existentes en este período serán marcadas para reagendar
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#8D99AE]" />
                  Fecha inicio
                </Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  data-testid="block-start-date-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Fecha fin</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  placeholder={formData.start_date}
                  data-testid="block-end-date-input"
                />
              </div>
            </div>

            <div className="flex items-center justify-between py-4 px-4 bg-[#F4F1EA] rounded-lg">
              <div>
                <Label className="font-medium">Día completo</Label>
                <p className="text-sm text-[#5D5C61]">Bloquear todo el día</p>
              </div>
              <Switch
                checked={formData.is_full_day}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_full_day: checked })
                }
                data-testid="block-fullday-switch"
              />
            </div>

            {!formData.is_full_day && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#8D99AE]" />
                    Hora inicio
                  </Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    data-testid="block-start-time-input"
                    required={!formData.is_full_day}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hora fin</Label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    data-testid="block-end-time-input"
                    required={!formData.is_full_day}
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Motivo</Label>
              <Input
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                placeholder="Vacaciones, congreso, etc..."
                data-testid="block-reason-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
              disabled={isLoading}
              data-testid="block-save-btn"
            >
              {isLoading ? 'Guardando...' : 'Crear Bloqueo'}
            </Button>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
